import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Paper
} from '@mui/material';
import { validate, warn } from './validate';
import { useThemeMode } from '../../utils/useThemeMode';

const AddJobForm = ({ onSubmitForm, errorMessage, isLoading }) => {
  const { mode } = useThemeMode();
  
  // React logo blue color for accents (same as in JobTile)
  const reactBlue = 'rgba(97, 218, 251, 0.9)';
  
  // Dynamic form styling based on theme
  const formStyle = {
    background: mode === 'dark' ? '#0A1929' : '#fff', // Same dark blue as ProgressTile
    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
    borderRadius: '15px',
    boxShadow: mode === 'dark' 
      ? `0px 0px 10px rgba(97, 218, 251, 0.2)` 
      : '0px 0px 10px rgba(0, 0, 0, 0.1)',
    padding: '30px',
    maxWidth: '700px',
    margin: '0 auto',
    border: mode === 'dark' ? `1px solid rgba(97, 218, 251, 0.1)` : 'none',
  };

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      title: '',
      company_name: '',
      url: '',
      location: '',
      post_date: '',
      note: ''
    },
    mode: 'onChange'
  });

  const onSubmit = (data) => {
    onSubmitForm(data); // Call Redux action provided by the container
  };

  const textFieldStyle = {
    input: { 
      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit' 
    },
    label: { 
      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'inherit' 
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.3)' : undefined
      },
      "&:hover fieldset": {
        borderColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.5)' : undefined
      },
      "&.Mui-focused fieldset": {
        borderColor: mode === 'dark' ? reactBlue : undefined
      }
    }
  };

  // Form fields configuration
  const fields = [
    { name: 'title', label: 'Job Title', required: true },
    { name: 'company_name', label: 'Company', required: true },
    { name: 'url', label: 'Job Posting URL', required: false },
    { name: 'location', label: 'Job Location', required: false },
    { name: 'post_date', label: 'Date Posted', required: false },
    { name: 'note', label: 'Notes', required: false }
  ];

  return (
    <Box component={Paper} elevation={3} sx={formStyle}>

      
      <Stack 
        component="form" 
        onSubmit={handleSubmit(onSubmit)} 
        spacing={3}
        sx={{ 
          '& .MuiTextField-root': { marginBottom: '1rem' } 
        }}
      >
        {fields.map(({ name, label, required }) => (
          <Controller
            key={name}
            name={name}
            control={control}
            rules={required ? validate[name] : warn[name]}
            render={({ field }) => (
              <TextField
                {...field}
                label={label}
                variant="outlined"
                fullWidth
                error={Boolean(errors[name])}
                helperText={errors[name] ? errors[name].message : ''}
                multiline={name === 'note'}
                rows={name === 'note' ? 4 : 1}
                type={name === 'post_date' ? 'date' : 'text'}
                InputLabelProps={
                  name === 'post_date' ? { shrink: true } : undefined
                }
                sx={textFieldStyle}
              />
            )}
          />
        ))}

        {/* Error Message */}
        {errorMessage && (
          <Typography 
            color="error" 
            align="center"
            sx={{ 
              padding: '10px',
              backgroundColor: mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(211, 47, 47, 0.05)',
              borderRadius: '4px',
              marginTop: '10px',
              marginBottom: '10px'
            }}
          >
            {errorMessage}
          </Typography>
        )}

        {/* Submit Button */}
        <Box textAlign="center">
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isSubmitting || isLoading}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
            sx={{
              marginTop: '1rem',
              padding: '10px 24px',
              fontSize: '1rem',
              backgroundColor: mode === 'dark' ? reactBlue : undefined,
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.8)' : undefined
              },
              boxShadow: mode === 'dark' ? '0 4px 12px rgba(97, 218, 251, 0.25)' : undefined,
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {isLoading ? 'Submitting...' : 'Track This Job'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default AddJobForm;
